import React, { useState } from 'react'
import { 
  Box, 
  TextField, 
  Typography, 
  Button, 
  Modal, 
  Paper,
  IconButton,
  Divider
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import useEmailEditorStore from '../../utilities/emailEditorStore';
import TypographyProps from './common/TypographyProps';
import BlockProps from './common/BlockProps';

function HtmlBlockProperties({blockId}) {
    const { getBlockById, updateBlockProperties } = useEmailEditorStore();
    const [modalOpen, setModalOpen] = useState(false);
    const [tempHtmlContent, setTempHtmlContent] = useState('');
  
    // Get the current block data from the store
    const block = getBlockById(blockId);
    
    if (!block) {
        return <Typography>Block not found</Typography>;
    }
    
    // Provide default properties if properties is undefined
    const properties = block.properties || {};

    // Generic property change handler
    const handlePropertyChange = (propertyName, value) => {
        updateBlockProperties(blockId, { [propertyName]: value });
    };

    const handleOpenModal = () => {
      setTempHtmlContent(properties.htmlContent || '');
      setModalOpen(true);
    };

    const handleSaveHtml = () => {
      handlePropertyChange('htmlContent', tempHtmlContent);
      setModalOpen(false);
    };

    const handleCancelModal = () => {
      setTempHtmlContent(properties.htmlContent || '');
      setModalOpen(false);
    };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>HTML Content</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={properties.htmlContent || ''}
          onChange={(e) => handlePropertyChange('htmlContent', e.target.value)}
          placeholder="Enter HTML code..."
          sx={{ mb: 1 }}
        />
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CodeIcon />}
          onClick={handleOpenModal}
        >
          Open Code Editor
        </Button>
      </Box>

      {/* HTML Code Editor Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCancelModal}
        aria-labelledby="html-editor-modal"
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: 900,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 24,
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeIcon color="primary" />
              <Typography variant="h6">HTML Code Editor</Typography>
            </Box>
            <IconButton onClick={handleCancelModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={20}
              value={tempHtmlContent}
              onChange={(e) => setTempHtmlContent(e.target.value)}
              placeholder="Enter your HTML code here..."
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.78rem',
                  lineHeight: 1.6,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Tip: Use proper HTML syntax. The code will be rendered in the email template.
            </Typography>
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button onClick={handleCancelModal} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSaveHtml} variant="contained" color="primary">
              Save Changes
            </Button>
          </Box>
        </Paper>
      </Modal>
      
      <Divider sx={{ my: 2 }} />
      
      <Box>
       <TypographyProps properties={properties} onPropertyChange={handlePropertyChange} />
       <BlockProps properties={properties} onPropertyChange={handlePropertyChange} />
      </Box>
    </>
  )
}

export default HtmlBlockProperties